import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { calculateRectangleCorners } from 'src/utils/map-calculations';

interface DumpsterRectangleOverlayProps {
  position: { lat: number; lng: number };
  rotation: number; // Degrees, 0-360
  lengthMeters: number; // Length of dumpster in meters
  widthMeters: number; // Width of dumpster in meters
  interactive?: boolean; // Enable drag to reposition
  onPositionChange?: (position: { lat: number; lng: number }) => void;
}

const ORANGE_COLOR = '#EA5B24';
const MIN_DISPLAY_SIZE_PX = 40; // Minimum size in pixels for visibility

export const DumpsterRectangleOverlay = ({
  position,
  rotation,
  lengthMeters,
  widthMeters,
  interactive = false,
  onPositionChange,
}: DumpsterRectangleOverlayProps) => {
  const map = useMap();
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Calculate rectangle corners
  const corners = useMemo(() => {
    return calculateRectangleCorners(position, lengthMeters, widthMeters, rotation);
  }, [position, lengthMeters, widthMeters, rotation]);

  // Create custom overlay
  useEffect(() => {
    if (!map) return;

    class DumpsterOverlay extends google.maps.OverlayView {
      private div: HTMLDivElement | null = null;

      onAdd() {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.pointerEvents = interactive ? 'auto' : 'none';
        div.style.cursor = interactive ? 'grab' : 'default';
        this.div = div;
        containerRef.current = div;

        const panes = this.getPanes();
        panes?.overlayLayer.appendChild(div);
      }

      draw() {
        if (!this.div) return;

        const projection = this.getProjection();
        if (!projection) return;

        // Convert all lat/lng points to pixel coordinates
        const cornerPixels = corners.map((corner) => {
          const point = projection.fromLatLngToDivPixel(
            new google.maps.LatLng(corner.lat, corner.lng),
          );
          return point || { x: 0, y: 0 };
        });

        // Calculate center point of the rectangle
        const centerX = cornerPixels.reduce((sum, p) => sum + p.x, 0) / cornerPixels.length;
        const centerY = cornerPixels.reduce((sum, p) => sum + p.y, 0) / cornerPixels.length;

        // Calculate actual dumpster dimensions in pixels (distance between adjacent corners)
        const dumpsterWidthPx = Math.sqrt(
          Math.pow(cornerPixels[1].x - cornerPixels[0].x, 2) +
            Math.pow(cornerPixels[1].y - cornerPixels[0].y, 2),
        );
        const dumpsterLengthPx = Math.sqrt(
          Math.pow(cornerPixels[2].x - cornerPixels[1].x, 2) +
            Math.pow(cornerPixels[2].y - cornerPixels[1].y, 2),
        );

        // Enforce minimum display size - scale from center to maintain perfect rectangle shape
        const scale = Math.max(
          1,
          MIN_DISPLAY_SIZE_PX / Math.max(dumpsterWidthPx, dumpsterLengthPx),
        );

        // Scale corners from center point to maintain exact rectangular shape
        const scaledCorners = cornerPixels.map((p) => ({
          x: centerX + (p.x - centerX) * scale,
          y: centerY + (p.y - centerY) * scale,
        }));

        // Calculate bounding box for positioning (using scaled corners)
        const minX = Math.min(...scaledCorners.map((p) => p.x));
        const minY = Math.min(...scaledCorners.map((p) => p.y));
        const maxX = Math.max(...scaledCorners.map((p) => p.x));
        const maxY = Math.max(...scaledCorners.map((p) => p.y));

        const width = maxX - minX;
        const height = maxY - minY;

        // Position the div
        this.div.style.left = `${minX}px`;
        this.div.style.top = `${minY}px`;
        this.div.style.width = `${width}px`;
        this.div.style.height = `${height}px`;

        // Adjust corner points relative to div position
        const adjustedCorners = scaledCorners.map((p) => ({
          x: p.x - minX,
          y: p.y - minY,
        }));

        // Update cursor during drag
        if (interactive) {
          this.div.style.cursor = isDragging ? 'grabbing' : 'grab';
        }

        // Create SVG with gradient styling for better visibility
        this.div.innerHTML = `
          <svg width="${width}" height="${height}" style="position: absolute; top: 0; left: 0;">
            <defs>
              <linearGradient id="dumpster-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:${ORANGE_COLOR};stop-opacity:0.75" />
                <stop offset="50%" style="stop-color:${ORANGE_COLOR};stop-opacity:0.64" />
                <stop offset="100%" style="stop-color:${ORANGE_COLOR};stop-opacity:0.53" />
              </linearGradient>
            </defs>
            <!-- Rectangle with gradient -->
            <polygon
              id="dumpster-rect"
              points="${adjustedCorners.map((p) => `${p.x},${p.y}`).join(' ')}"
              fill="url(#dumpster-gradient)"
              stroke="${ORANGE_COLOR}"
              stroke-width="2"
              style="cursor: ${interactive ? (isDragging ? 'grabbing' : 'grab') : 'default'};"
            />
          </svg>
        `;
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div);
          this.div = null;
        }
        containerRef.current = null;
      }
    }

    const overlay = new DumpsterOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, corners, interactive, isDragging]);

  // Attach drag event listeners directly to the container div
  useEffect(() => {
    if (!interactive || !containerRef.current) return;

    const container = containerRef.current;
    let localDragStart: { x: number; y: number; lat: number; lng: number } | null = null;

    const handleMouseDown = (e: MouseEvent) => {
      if (!map) return;

      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);
      localDragStart = {
        x: e.clientX,
        y: e.clientY,
        lat: position.lat,
        lng: position.lng,
      };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!localDragStart || !map) return;

      e.preventDefault();

      const deltaX = e.clientX - localDragStart.x;
      const deltaY = e.clientY - localDragStart.y;

      // Convert pixel delta to lat/lng delta
      const projection = map.getProjection();
      if (!projection) return;

      const scale = Math.pow(2, map.getZoom() || 15);
      const worldCoordinate = projection.fromLatLngToPoint(
        new google.maps.LatLng(localDragStart.lat, localDragStart.lng),
      );

      if (!worldCoordinate) return;

      const newWorldX = worldCoordinate.x + deltaX / scale;
      const newWorldY = worldCoordinate.y + deltaY / scale;

      const newLatLng = projection.fromPointToLatLng(new google.maps.Point(newWorldX, newWorldY));

      if (newLatLng && onPositionChange) {
        onPositionChange({
          lat: Number.parseFloat(newLatLng.lat().toFixed(6)),
          lng: Number.parseFloat(newLatLng.lng().toFixed(6)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      localDragStart = null;
    };

    // Attach listeners
    container.addEventListener('mousedown', handleMouseDown as EventListener);
    document.addEventListener('mousemove', handleMouseMove as EventListener);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown as EventListener);
      document.removeEventListener('mousemove', handleMouseMove as EventListener);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [interactive, map, position, onPositionChange]);

  // Redraw when position or rotation changes
  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.draw();
    }
  }, [position, rotation, isDragging]);

  return null; // This component doesn't render React elements
};
